/** Thin fetch wrapper for the /api routes: JSON in/out, CSRF header, typed errors. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type QueryValue = string | number | boolean | null | undefined | (string | number)[];

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  form?: FormData;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
}

/** Repeats array values (`company=1&company=2`) and skips empty ones. */
export function buildQuery(query: Record<string, QueryValue> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const v of value) params.append(key, String(v));
    } else {
      params.append(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

/** Fired when the API answers 401 to an authenticated call: the session expired or was revoked. */
export const AUTH_EXPIRED_EVENT = "auth:expired";

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  // Mutations must carry this header: the API's CSRF guard rejects them otherwise.
  if (method !== "GET") headers["x-requested-with"] = "fetch";
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body);
  }
  const response = await fetch(path + buildQuery(options.query), {
    method,
    headers,
    credentials: "same-origin",
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  if (!response.ok) {
    const err = (
      payload as { error?: { code?: string; message?: string; details?: unknown } } | null
    )?.error;
    // Auth endpoints answer 401 for a wrong (current) password; that is not an expired session.
    if (response.status === 401 && !path.startsWith("/api/auth/")) {
      window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(
      response.status,
      err?.code ?? `http_${response.status}`,
      err?.message ?? response.statusText,
      err?.details,
    );
  }
  return payload as T;
}
